import { FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import classnames from 'classnames';
import React__default from 'react';
import { useDropdownState } from './use_dropdown_state.js';
import '../box/index.js';
import '../list/index.js';
import { menu, size, placement, animationStatus } from './dropdown.css.js';
import { Box } from '../box/box.js';
import { List } from '../list/list.js';

/**
 * Toggleable list of actions that DropdownButton controls.
 */
const DropdownMenu = ({ children, className, size: size$1 = 'small', ...rest }) => {
    const { id, open, setOpen, isMounted, transitionStatus, refs, placement: placement$1, getFloatingProps, context, ...data } = useDropdownState();
    const [focusedItemIndex, setFocusedItemIndex] = React__default.useState(-1);
    const listElement = refs.floating.current;
    React__default.useEffect(() => {
        // Delay focusing the item so that positioning can happen before. Without the timeout,
        // the focusing of the first item after opening would jump the page scroll to the bottom of the body.
        const timeout = setTimeout(() => {
            if (open) {
                // Focus the first item when the menu is opened...
                setFocusedItemIndex(0);
            }
            else {
                // ...and reset the value when the menu is closed.
                setFocusedItemIndex(-1);
            }
        });
        return () => clearTimeout(timeout);
    }, [open]);
    React__default.useEffect(() => {
        if (focusedItemIndex >= 0) {
            const items = listElement?.querySelectorAll('[role="menuitem"]') || [];
            items[focusedItemIndex]?.focus();
        }
    }, [focusedItemIndex, listElement]);
    const handleKeyDown = React__default.useCallback((event) => {
        let isCustomBehavior = false;
        switch (event.key) {
            case 'ArrowDown': {
                isCustomBehavior = true;
                const items = listElement?.querySelectorAll('[role="menuitem"]') || [];
                const nextFocusedIndex = focusedItemIndex === items.length - 1 ? 0 : Math.min(focusedItemIndex + 1, items.length - 1);
                setFocusedItemIndex(nextFocusedIndex);
                break;
            }
            case 'ArrowUp': {
                isCustomBehavior = true;
                const items = listElement?.querySelectorAll('[role="menuitem"]') || [];
                const nextFocusedIndex = focusedItemIndex === 0 ? items.length - 1 : Math.max(focusedItemIndex - 1, 0);
                setFocusedItemIndex(nextFocusedIndex);
                break;
            }
            case 'Home': {
                isCustomBehavior = true;
                setFocusedItemIndex(0);
                break;
            }
            case 'End': {
                isCustomBehavior = true;
                const items = listElement?.querySelectorAll('[role="menuitem"]') || [];
                setFocusedItemIndex(items.length - 1);
                break;
            }
        }
        if (isCustomBehavior) {
            event.stopPropagation();
            event.preventDefault();
        }
    }, [listElement, focusedItemIndex]);
    const side = placement$1.split('-')[0];
    return isMounted ? (React__default.createElement(FloatingPortal, null,
        React__default.createElement(FloatingFocusManager, { context: context, initialFocus: -1, modal: false },
            React__default.createElement(Box, { ...rest, className: classnames(menu, size[size$1], placement[side], animationStatus[transitionStatus], className), ref: refs.setFloating, ...getFloatingProps({ ...rest, onKeyDown: handleKeyDown }), style: {
                    position: data.strategy ?? 'absolute',
                    top: data.y ?? '',
                    left: data.x ?? '',
                }, padding: "small", elevation: "overlay", radius: "small" },
                React__default.createElement(List, { id: `${id}-menu`, role: "menu", "aria-labelledby": `${id}-button`, gap: "xsmall" }, children))))) : null;
};

export { DropdownMenu };
